import { DataRow, FontFormat } from '@23degrees/shared-code/models';
import { DeltaInsertOp } from '../renderer/DeltaInsertOp';
import { OpToHtmlConverter } from '../renderer/OpToHtmlConverter';
import { QuillDeltaToHtmlConverter } from '../renderer/QuillDeltaToHtmlConverter';
import { convertFontFormat, styleToCSS } from './format';
import { InlineMedia } from './InlineMedia';
import { InlineReference } from './InlineReference';
import { InlineValue } from './InlineValue';
import { InlineVariable } from './InlineVariable';
import { DynamicLink } from './DynamicLink';

export interface QuillDelta {
  ops: DeltaInsertOp[];
  meta?: {
    [key: string]: any;
  };
}

/**
 * convertDeltaToHTML
 *
 * @param {string} quillDelta Required. Delta quill object
 * @param {FontFormat} styleObj Required. Object with the global styles
 * @param {DataRow} dataRow Required. Object with fields as keys and its respective values
 * @param {number} [sizeFactor=1] Optional.
 * @param {any} content Optional.
 * @param {ObjectOrNull} customStyle Optional.
 * @param {ObjectOrNull} customFormat Optional. Used for mixed types
 * @param {(link: string) => string} escapeMediaLink Optional. Used to set images urls
 * @return {string} HTML string
 */

export function convertDeltaToHTML(
  quillDelta: string,
  styleObj: FontFormat,
  dataRow: DataRow,
  sizeFactor = 1,
  content?: any,
  customStyle: Record<string, string | number> | null = {},
  escapeMediaLink?: (link: string) => string,  
  customFormat: Record<string, string | number> | null = null,
) {
  const renderInlineValue = (customOp: any) => {
    try {
      if (customOp.insert.type === 'inlineValue') {
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor });
        // const classes = opToHtml.getCssClasses();
        // const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();
        const inlineValue = InlineValue.create(customOp);
        let inlineStr = inlineValue.resolveFields(dataRow, '', content, customFormat);
        inlineStr = inlineStr.replace(/-/g, '&#8209;');

        return html.replace('[object Object]', inlineStr);
      }
      if (customOp.insert.type === 'inlineReference') {
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor });
        // const classes = opToHtml.getCssClasses();
        // const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();

        const inlineValue = InlineReference.create(customOp);
        let inlineStr = inlineValue.resolveFieldsReference(dataRow, '', content, customFormat);
        inlineStr = inlineStr.replace(/-/g, '&#8209;');

        return html.replace('[object Object]', inlineStr);
      }
      if (customOp.insert.type === 'dynamicImage') {
        /**
         * Render Image From Data Field
         */
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        const styles = opToHtml.getCssStyles();
        const verticalAlign = val.imageFormat.verticalAlign ?? 'middle';
        styles.push(`vertical-align: ${verticalAlign}`);
        // const html = opToHtml.getHtml();
        const inlineMedia = InlineMedia.create(customOp);
        const inlineStr = inlineMedia.resolveFieldsReference(dataRow, '', content);
        const html = inlineMedia.getHTML(inlineStr, escapeMediaLink, val.imageFormat, customOp.attributes);
        return `<span style="${styles.join(';')}" data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${html}</span>`;
      }
      if (customOp.insert.type === 'inlineVariable') {
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor });
        // const classes = opToHtml.getCssClasses();
        // const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();
        const inlineVariable = InlineVariable.create(customOp);
        let inlineStr = inlineVariable.resolveVariables(dataRow, '');
        inlineStr = inlineStr.replace(/-/g, '&#8209;');
        return html.replace('[object Object]', inlineStr);
      }

      if (customOp.insert.type === 'dynamicLink') {
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        classes.push('delta-container'); // To match same as in ToolTip
        const styles = opToHtml.getCssStyles();
        // const html = opToHtml.getHtml();
        const dynamicLink = DynamicLink.create(customOp);
        const inlineStr = dynamicLink.resolveFields(dataRow, '', content, customFormat);
        const { italic, underline } = customOp.attributes ?? {};
        if (italic) styles.push('font-style: italic');
        if (underline) styles.push('text-decoration: underline');
        return `<span style="${styles.join(';')}" data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${inlineStr}</span>`;
      }

      return 'Unmanaged custom blot!';
    } catch (error) {
      console.log('[QuillDeltaToHtmlConverter] error:', error);
      return 'Error in custom blot!';
    }
  };

  const htmlStyle = convertFontFormat(styleObj);
  // biome-ignore lint: color is passed in the customStyle object
  delete htmlStyle.color;

  const cssStyle = styleToCSS({ ...htmlStyle, ...customStyle });

  let delta: QuillDelta = { ops: [] };
  if (typeof quillDelta === 'string') {
    delta = JSON.parse(quillDelta);
  }

  const converter = new QuillDeltaToHtmlConverter(delta.ops, { inlineStyles: true, sizeFactor, escapeLink: escapeMediaLink });
  converter.renderCustomWith(renderInlineValue);

  const htmlStr = `<div style="${cssStyle}">${converter.convert()}</div>`;
  return htmlStr;
}
